import { HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi";

const DEFAULT_PAGE_SIZE_OPTIONS = [20, 50, 100, 500, 1000];

/**
 * Reusable pagination component for all listing pages.
 *
 * Props:
 * currentPage - current 1-based page number
 * totalPages - total number of pages
 * totalCount - total number of records
 * pageSize - items per page
 * onPageChange - (newPage) => void
 * entityName - e.g. "states", "schools" (for display)
 * onPageSizeChange - optional (newPageSize) => void — when provided, renders a
 *   "Rows per page" dropdown (20/50/100/500/1000, capped at 1000); omit to
 *   keep a page's pagination footer fixed-size.
 * pageSizeOptions - optional override for the dropdown's choices (default [20,50,100,500,1000])
 */
function Pagination({ currentPage, totalPages, totalCount, pageSize, onPageChange, onPageSizeChange, pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS, entityName = "records" }) {
 if (totalCount === 0) return null;

 const start = (currentPage - 1) * pageSize + 1;
 const end = Math.min(currentPage * pageSize, totalCount);

 // Build page numbers to display (max 5 around current)
 const pages = [];
 let startPage = Math.max(1, currentPage - 2);
 let endPage = Math.min(totalPages, currentPage + 2);
 if (endPage - startPage < 4) {
 if (startPage === 1) endPage = Math.min(totalPages, startPage + 4);
 else startPage = Math.max(1, endPage - 4);
 }
 for (let i = startPage; i <= endPage; i++) pages.push(i);

 // Always include the current pageSize as an option, even if it isn't one of
 // the defaults (e.g. a page's default page size differs from the presets).
 const sizeOptions = pageSizeOptions.includes(pageSize)
 ? pageSizeOptions
 : [...pageSizeOptions, pageSize].sort((a, b) => a - b);

 return (
 <div className="flex items-center justify-between px-6 py-3 border-t border-[#e5e7eb] bg-white gap-4 flex-wrap">
 <div className="flex items-center gap-4">
 <span className="text-xs text-[#57606a] whitespace-nowrap">
 Showing {start}–{end} of {totalCount} {entityName}
 </span>
 {onPageSizeChange && (
 <label className="flex items-center gap-1.5 text-xs text-[#57606a] whitespace-nowrap">
 Rows per page
 <select
 value={pageSize}
 onChange={(e) => onPageSizeChange(Number(e.target.value))}
 className="h-7 pl-2 pr-6 text-xs border border-[#d0d5dd] rounded-md bg-white text-[#24292f] cursor-pointer focus:outline-none focus:border-[#0078d4] focus:ring-1 focus:ring-[#0078d4]/20"
 >
 {sizeOptions.map((n) => (
 <option key={n} value={n}>{n}</option>
 ))}
 </select>
 </label>
 )}
 </div>
 <div className="flex items-center gap-1">
 <button
 type="button"
 className="w-8 h-8 flex items-center justify-center rounded-md text-[#57606a] hover:bg-[#f3f4f6] disabled:opacity-30 disabled:cursor-not-allowed border-none bg-transparent cursor-pointer transition-colors"
 onClick={() => onPageChange(currentPage - 1)}
 disabled={currentPage <= 1}
 title="Previous page"
 >
 <HiOutlineChevronLeft className="text-sm" />
 </button>

 {startPage > 1 && (
 <>
 <button
 type="button"
 className="w-8 h-8 flex items-center justify-center rounded-md text-xs font-medium text-[#57606a] hover:bg-[#f3f4f6] border-none bg-transparent cursor-pointer transition-colors"
 onClick={() => onPageChange(1)}
 >
 1
 </button>
 {startPage > 2 && (
 <span className="w-6 text-center text-xs text-[#8b949e]">…</span>
 )}
 </>
 )}

 {pages.map((p) => (
 <button
 key={p}
 type="button"
 className={`w-8 h-8 flex items-center justify-center rounded-md text-xs font-medium border-none cursor-pointer transition-colors ${
 p === currentPage
 ? "bg-[#0078d4] text-white"
 : "text-[#57606a] hover:bg-[#f3f4f6] bg-transparent"
 }`}
 onClick={() => onPageChange(p)}
 >
 {p}
 </button>
 ))}

 {endPage < totalPages && (
 <>
 {endPage < totalPages - 1 && (
 <span className="w-6 text-center text-xs text-[#8b949e]">…</span>
 )}
 <button
 type="button"
 className="w-8 h-8 flex items-center justify-center rounded-md text-xs font-medium text-[#57606a] hover:bg-[#f3f4f6] border-none bg-transparent cursor-pointer transition-colors"
 onClick={() => onPageChange(totalPages)}
 >
 {totalPages}
 </button>
 </>
 )}

 <button
 type="button"
 className="w-8 h-8 flex items-center justify-center rounded-md text-[#57606a] hover:bg-[#f3f4f6] disabled:opacity-30 disabled:cursor-not-allowed border-none bg-transparent cursor-pointer transition-colors"
 onClick={() => onPageChange(currentPage + 1)}
 disabled={currentPage >= totalPages}
 title="Next page"
 >
 <HiOutlineChevronRight className="text-sm" />
 </button>
 </div>
 </div>
 );
}

export default Pagination;
